import { useEffect, useState, useRef } from 'react'
import { Plus, Search, X, UserMinus, ChevronDown, Upload, Trash2, ArrowUp, ArrowDown } from 'lucide-react'
import { clientsAPI, cardsAPI } from '../api/client'
import { useOrg } from '../contexts/OrgContext'
import NoteModal from '../components/NoteModal'

function ClientsPage() {
  const { currentOrg } = useOrg()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [isEditingClient, setIsEditingClient] = useState(false)
  const [editClientData, setEditClientData] = useState({})
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    client_status: 'new-client',
    internal_note: '',
    external_note: '',
    membership_plan: '',
    payment_method: 'none',
    payment_status: 'none',
    last_payment_date: '',
    last_payment_amount: '',
    next_billing_date: ''
  })

  // Phone formatting helper
  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  const normalizePhone = (value) => {
    return value.replace(/\D/g, '').slice(0, 10);
  }
  const [submitting, setSubmitting] = useState(false)
  const [statusChangeWarning, setStatusChangeWarning] = useState('')
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [noteModalClient, setNoteModalClient] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [showAdvancedMenu, setShowAdvancedMenu] = useState(false)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [statusFilter, setStatusFilter] = useState('all')
  const [hideInactive, setHideInactive] = useState(true)
  const [includeHiddenInSearch, setIncludeHiddenInSearch] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const advancedMenuRef = useRef(null)
  const [csvFile, setCsvFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [importErrors, setImportErrors] = useState([])
  const [importSuccess, setImportSuccess] = useState([])
  const [importing, setImporting] = useState(false)
  const [editingErrorIndex, setEditingErrorIndex] = useState(null)
  const [selectedForDelete, setSelectedForDelete] = useState([])
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [unassignCards, setUnassignCards] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [bulkDeleteSearch, setBulkDeleteSearch] = useState('')

  // Pagination state
  const [pageSize, setPageSize] = useState(100)
  const [totalClients, setTotalClients] = useState(0)

  // Close advanced menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (advancedMenuRef.current && !advancedMenuRef.current.contains(event.target)) {
        setShowAdvancedMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (currentOrg?.org_id) {
      loadClients()
    }
  }, [currentOrg?.org_id])

  async function loadClients() {
    if (!currentOrg?.org_id) return

    try {
      setLoading(true)
      setError('')

      // Load all clients with pagination
      let allClients = []
      let nextToken = null
      do {
        const params = { org_id: currentOrg.org_id, limit: 100 }
        if (nextToken) params.nextToken = nextToken
        const response = await clientsAPI.getAll(params)
        const clientsData = response.data?.clients || []
        allClients = [...allClients, ...clientsData]
        nextToken = response.data?.nextToken
      } while (nextToken)

      setClients(allClients)
      setTotalClients(allClients.length)
    } catch (err) {
      setError(err.message || 'Failed to load clients')
      console.error('Clients error:', err)
    } finally {
      setLoading(false)
    }
  }

  function validateEmail(email) {
    // Reject commas and other invalid characters
    if (email.includes(',')) return false
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  function capitalizeName(name) {
    // Capitalize first letter of each word, handle multi-word names
    if (!name) return name
    return name
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    // Client-side validation
    const errors = {}

    if (!formData.email) {
      errors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!formData.first_name) {
      errors.first_name = 'First name is required'
    }

    if (!formData.last_name) {
      errors.last_name = 'Last name is required'
    }

    if (formData.mobile) {
      const cleaned = normalizePhone(formData.mobile)
      if (cleaned.length > 0 && cleaned.length !== 10) {
        errors.mobile = 'Phone number must be exactly 10 digits'
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setSubmitting(false)
      return
    }

    // Clear any previous field errors since validation passed
    setFieldErrors({})

    try {
      const submitData = {
        ...formData,
        mobile: normalizePhone(formData.mobile),
        email: formData.email.toLowerCase().trim(),
        org_id: currentOrg.org_id
      }
      await clientsAPI.create(submitData)

      // Only reset form and close modal on success
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
        client_status: 'new-client',
        internal_note: '',
        external_note: '',
        membership_plan: '',
        payment_method: 'none',
        payment_status: 'none',
        last_payment_date: '',
        last_payment_amount: '',
        next_billing_date: ''
      })
      setFieldErrors({})
      setShowModal(false)
      await loadClients()
    } catch (err) {
      setError(err.message || 'Failed to create client')
      // Don't reset form on error - keep user's input
    } finally {
      setSubmitting(false)
    }
  }

  function handleViewDetails(client) {
    setSelectedClient(client)
    setEditClientData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      mobile: client.mobile || '',
      status_rfid: client.status_rfid || 'inactive',
      client_status: client.client_status || 'new-client',
      internal_note: client.internal_note || '',
      external_note: client.external_note || '',
      membership_plan: client.membership_plan || '',
      payment_method: client.payment_method || 'none',
      payment_status: client.payment_status || 'none',
      last_payment_date: client.last_payment_date || '',
      last_payment_amount: client.last_payment_amount || '',
      next_billing_date: client.next_billing_date || ''
    })
    setIsEditingClient(false)
    setStatusChangeWarning('')
    setError('')
    setShowDetailsModal(true)
  }

  async function handleSaveClientStatus() {
    setError('')
    setSubmitting(true)

    try {
      // Determine if client is active or inactive based on client_status
      const activeStatuses = ['current', 'access-only', 'bolo', 'probation']
      const isActive = activeStatuses.includes(editClientData.client_status)

      // Update client with new status_rfid based on client_status
      const updatedData = {
        ...editClientData,
        mobile: normalizePhone(editClientData.mobile),
        email: editClientData.email.toLowerCase().trim(),
        status_rfid: isActive ? 'active' : 'inactive'
      }

      await clientsAPI.update(selectedClient.client_id, updatedData)
      setIsEditingClient(false)
      await loadClients()

      // Immediately update selectedClient with edited data
      setSelectedClient({
        ...selectedClient,
        ...updatedData
      })
    } catch (err) {
      setError(err.message || 'Failed to update client status')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDeleteClient() {
    if (!selectedClient) return

    // Block delete if card is assigned
    if (selectedClient.rfid_card_number) {
      setError(`Cannot delete: Client has card ${selectedClient.rfid_card_number.slice(-5)} assigned. Unassign the card first.`)
      return
    }

    // Show confirmation warning
    const clientName = `${selectedClient.first_name} ${selectedClient.last_name}`
    const confirmed = window.confirm(
      `⚠️ Are you sure you want to delete client "${clientName}" (ID: ${selectedClient.client_id})?\n\nThis action cannot be undone.`
    )

    if (!confirmed) {
      return
    }

    setError('')
    setSubmitting(true)

    try {
      await clientsAPI.delete(selectedClient.client_id)
      setShowDetailsModal(false)
      setSelectedClient(null)
      setIsEditingClient(false)
      await loadClients()
    } catch (err) {
      setError(err.message || 'Failed to delete client')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleUnassignCardFromClient() {
    if (!selectedClient?.rfid_card_number) return

    setError('')
    setSubmitting(true)

    try {
      await cardsAPI.unassign(selectedClient.rfid_card_number)
      await loadClients()

      // Update selectedClient to reflect the change
      setSelectedClient({
        ...selectedClient,
        rfid_card_number: null,
        status_rfid: 'inactive'
      })
    } catch (err) {
      setError(err.message || 'Failed to unassign card')
    } finally {
      setSubmitting(false)
    }
  }

  function handleOpenNoteModal(client) {
    setNoteModalClient(client)
    setShowNoteModal(true)
  }

  async function handleSaveNote(updatedClient) {
    setError('')

    try {
      await clientsAPI.update(updatedClient.client_id, {
        internal_note: updatedClient.internal_note,
        external_note: updatedClient.external_note
      })

      await loadClients()

      // Update the noteModalClient with the new data
      setNoteModalClient(updatedClient)
    } catch (err) {
      setError(err.message || 'Failed to update note')
    }
  }

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new column and default to ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Filter clients based on search, status, and hide options
  let allFilteredClients = clients.filter(client => {
    // Status filter
    if (statusFilter !== 'all' && client.client_status !== statusFilter) {
      return false
    }

    // Hide canceled/blocked unless search includes them or user opted in
    if (hideInactive && !includeHiddenInSearch &&
        (client.client_status === 'canceled' || client.client_status === 'blocked')) {
      return false
    }

    // Search term filter
    if (searchTerm) {
      const searchString = `${client.first_name} ${client.last_name} ${client.email} ${client.mobile} ${client.client_id}`
        .toLowerCase()

      // If searching and includeHiddenInSearch is true, show hidden statuses in search results
      if (!searchString.includes(searchTerm.toLowerCase())) {
        return false
      }
    }

    return true
  })

  // Sort filtered clients if sort column is set
  if (sortColumn) {
    allFilteredClients = [...allFilteredClients].sort((a, b) => {
      let aVal, bVal

      switch (sortColumn) {
        case 'client_id':
          aVal = a.client_id || ''
          bVal = b.client_id || ''
          break
        case 'name':
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase()
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase()
          break
        case 'email':
          aVal = a.email || ''
          bVal = b.email || ''
          break
        case 'mobile':
          aVal = a.mobile || ''
          bVal = b.mobile || ''
          break
        case 'card_number':
          aVal = a.rfid_card_number || ''
          bVal = b.rfid_card_number || ''
          break
        case 'status_rfid':
          aVal = a.status_rfid || ''
          bVal = b.status_rfid || ''
          break
        case 'client_status':
          aVal = a.client_status || ''
          bVal = b.client_status || ''
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }

  // Apply client-side pagination
  const filteredClients = pageSize >= allFilteredClients.length
    ? allFilteredClients
    : allFilteredClients.slice(0, pageSize)

  // CSV Import handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file')
      return
    }

    setCsvFile(file)
    parseCSV(file)
  }

  const parseCSV = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target.result
      const lines = text.split('\n').map(line => line.trim()).filter(line => line)

      if (lines.length < 2) {
        setError('CSV file is empty or has no data rows')
        return
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
      const requiredHeaders = ['first_name', 'last_name', 'email', 'mobile']

      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
      if (missingHeaders.length > 0) {
        setError(`Missing required columns: ${missingHeaders.join(', ')}`)
        return
      }

      const rows = []
      const errors = []

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim())
        const row = {}

        headers.forEach((header, index) => {
          row[header] = values[index] || ''
        })

        // Capitalize first and last names for better UX
        if (row.first_name) row.first_name = capitalizeName(row.first_name)
        if (row.last_name) row.last_name = capitalizeName(row.last_name)

        // Validate row
        const rowErrors = []
        if (!row.first_name) rowErrors.push('Missing first_name')
        if (!row.last_name) rowErrors.push('Missing last_name')
        if (!row.email) rowErrors.push('Missing email')
        if (row.email && !validateEmail(row.email)) rowErrors.push('Invalid email format')
        if (row.mobile && !/^\d{10}$/.test(row.mobile.replace(/\D/g, ''))) rowErrors.push('Invalid mobile (must be 10 digits)')

        // Check for duplicates in existing clients
        const isDuplicate = clients.some(c =>
          c.email.toLowerCase() === row.email.toLowerCase() ||
          (c.first_name.toLowerCase() === row.first_name.toLowerCase() &&
           c.last_name.toLowerCase() === row.last_name.toLowerCase())
        )

        if (isDuplicate) {
          rowErrors.push('Duplicate: Client already exists with this email or name')
        }

        // Note: Card validation will happen during import, not during parsing
        // This allows us to fetch fresh card data at import time

        if (rowErrors.length > 0) {
          errors.push({ row: i, data: row, errors: rowErrors })
        } else {
          rows.push({ row: i, data: row, valid: true })
        }
      }

      setImportPreview(rows)
      setImportErrors(errors)
    }

    reader.readAsText(file)
  }

  const handleImport = async () => {
    if (!importPreview || importPreview.length === 0) {
      setError('No valid rows to import')
      return
    }

    setImporting(true)
    const successfulImports = []
    const failedImports = []

    // Fetch all cards once for card_id lookups
    let allCards = []
    try {
      let nextToken = null
      do {
        const params = { org_id: currentOrg.org_id, limit: 100 }
        if (nextToken) params.nextToken = nextToken
        const response = await cardsAPI.getAll(params)
        const cardsData = response.data?.cards || []
        allCards = [...allCards, ...cardsData]
        nextToken = response.data?.nextToken
      } while (nextToken)
      console.log('[ClientsPage Import] Fetched cards for assignment:', allCards.length)
    } catch (err) {
      console.error('[ClientsPage Import] Failed to fetch cards:', err)
      // Continue with import even if cards fetch fails
    }

    for (const item of importPreview) {
      try {
        const clientData = {
          first_name: item.data.first_name,
          last_name: item.data.last_name,
          email: item.data.email.toLowerCase().trim(),
          mobile: normalizePhone(item.data.mobile),
          client_status: item.data.client_status || 'new-client',
          org_id: currentOrg.org_id,
          // Legacy and additional fields (optional)
          legacy_client_id: item.data.legacy_client_id || null,
          legacy_membership_plan: item.data.legacy_membership_plan || null,
          hasMembership: item.data.hasMembership || null,
          cardLastValidated: item.data.cardLastValidated || null,
          internal_note: item.data.internal_note || null,
          // Payment tracking fields (optional)
          membership_plan: item.data.membership_plan || null,
          payment_method: item.data.payment_method || 'none',
          payment_status: item.data.payment_status || 'none',
          last_payment_date: item.data.last_payment_date || null,
          last_payment_amount: item.data.last_payment_amount ? Number(item.data.last_payment_amount) : null,
          next_billing_date: item.data.next_billing_date || null
        }

        // Create the client first
        console.log('[ClientsPage Import] Creating client:', clientData.email)
        const createResponse = await clientsAPI.create(clientData)
        const createdClient = createResponse.data?.client
        console.log('[ClientsPage Import] Client created with ID:', createdClient?.client_id)

        // If card_id is provided, try to assign the card
        if (item.data.card_id && item.data.card_id.trim()) {
          const cardIdInput = item.data.card_id.trim()
          console.log('[ClientsPage Import] Attempting to assign card:', cardIdInput)

          // Find matching card by full ID or last 5 digits
          let matchingCard = null
          if (cardIdInput.length >= 10) {
            // Try exact match first (using card_id field, not rfid_card_number)
            matchingCard = allCards.find(card => card.card_id === cardIdInput)
            console.log('[ClientsPage Import] Exact match by card_id:', matchingCard ? 'Found' : 'Not found')
          }

          if (!matchingCard && cardIdInput.length >= 1) {
            // Try matching by last 5 digits (using last_5_digits field)
            const cardsMatchingLast5 = allCards.filter(card =>
              card.last_5_digits === cardIdInput.slice(-5)
            )
            console.log('[ClientsPage Import] Last 5 digits match count:', cardsMatchingLast5.length)

            if (cardsMatchingLast5.length === 1) {
              matchingCard = cardsMatchingLast5[0]
            } else if (cardsMatchingLast5.length > 1) {
              throw new Error(`Card ID '${cardIdInput}' matches multiple cards. Please use full card ID.`)
            }
          }

          if (!matchingCard) {
            console.error('[ClientsPage Import] Card not found:', cardIdInput)
            throw new Error(`Card '${cardIdInput}' not found`)
          }

          console.log('[ClientsPage Import] Matching card found:', matchingCard.card_id)

          // Check if card is already assigned
          if (matchingCard.assigned_to || matchingCard.client_id) {
            console.error('[ClientsPage Import] Card already assigned:', matchingCard.card_id)
            throw new Error(`Card ${matchingCard.last_5_digits} is already assigned`)
          }

          // Assign the card to the newly created client (using card_id field)
          try {
            console.log('[ClientsPage Import] Assigning card', matchingCard.card_id, 'to client', createdClient.client_id)
            await cardsAPI.assign(matchingCard.card_id, {
              client_id: createdClient.client_id
            })
            console.log('[ClientsPage Import] Card assignment successful')
          } catch (assignErr) {
            // Card assignment failed, but client was created
            console.error('[ClientsPage Import] Card assignment failed:', assignErr)
            throw new Error(`Client created but card assignment failed: ${assignErr.message}`)
          }
        }

        successfulImports.push(item)
      } catch (err) {
        console.error('[ClientsPage Import] Row import failed:', err.message)
        failedImports.push({ ...item, error: err.message })
      }
    }

    console.log('[ClientsPage Import] Import complete. Success:', successfulImports.length, 'Failed:', failedImports.length)
    setImportSuccess(successfulImports)
    if (failedImports.length > 0) {
      setImportErrors(failedImports)
    }

    // Clear the valid rows preview after successful import
    if (successfulImports.length > 0) {
      setImportPreview(null)
    }

    setImporting(false)
    await loadClients()
  }

  const resetImport = () => {
    setCsvFile(null)
    setImportPreview(null)
    setImportErrors([])
    setImportSuccess([])
    setShowImportModal(false)
  }

  const downloadSampleCSV = () => {
    const sampleData = `first_name,last_name,email,mobile,client_status,card_id,legacy_client_id,legacy_membership_plan,hasMembership,cardLastValidated,internal_note,membership_plan,payment_method,payment_status,last_payment_date,last_payment_amount,next_billing_date
John,Doe,john.doe@example.com,8161234567,new-client,12345,101,Gold Member,yes,2025-01-01,Staff note here,Monthly,stripe_card,current,2025-01-15,99.99,2025-02-15
Jane,Smith,jane.smith@example.com,8169876543,current,0012345678901234,205,Silver Plan,no,2024-12-15,Important client,Annual,venmo,current,2024-12-01,999.00,2025-12-01`

    const blob = new Blob([sampleData], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sample-clients-import.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const updateErrorRow = (index, field, value) => {
    const updatedErrors = [...importErrors]
    updatedErrors[index].data[field] = value
    setImportErrors(updatedErrors)
  }

  const validateErrorRow = (rowData) => {
    const errors = []
    if (!rowData.first_name) errors.push('Missing first_name')
    if (!rowData.last_name) errors.push('Missing last_name')
    if (!rowData.email) errors.push('Missing email')
    if (rowData.email && !validateEmail(rowData.email)) errors.push('Invalid email format')
    if (rowData.mobile && !/^\d{10}$/.test(rowData.mobile.replace(/\D/g, ''))) errors.push('Invalid mobile (must be 10 digits)')

    // Check for duplicates in existing clients
    const isDuplicate = clients.some(c =>
      c.email.toLowerCase() === rowData.email.toLowerCase() ||
      (c.first_name.toLowerCase() === rowData.first_name.toLowerCase() &&
       c.last_name.toLowerCase() === rowData.last_name.toLowerCase())
    )

    if (isDuplicate) {
      errors.push('Duplicate: Client already exists with this email or name')
    }

    return errors
  }

  const retryImportErrorRow = async (index) => {
    const errorItem = importErrors[index]
    const validationErrors = validateErrorRow(errorItem.data)

    if (validationErrors.length > 0) {
      // Update the errors for this row
      const updatedErrors = [...importErrors]
      updatedErrors[index].errors = validationErrors
      setImportErrors(updatedErrors)
      return
    }

    // Try to import this row
    setImporting(true)
    try {
      const clientData = {
        first_name: errorItem.data.first_name,
        last_name: errorItem.data.last_name,
        email: errorItem.data.email.toLowerCase().trim(),
        mobile: normalizePhone(errorItem.data.mobile),
        client_status: errorItem.data.client_status || 'new-client',
        org_id: currentOrg.org_id,
        // Payment tracking fields (optional)
        membership_plan: errorItem.data.membership_plan || null,
        payment_method: errorItem.data.payment_method || 'none',
        payment_status: errorItem.data.payment_status || 'none',
        last_payment_date: errorItem.data.last_payment_date || null,
        last_payment_amount: errorItem.data.last_payment_amount ? Number(errorItem.data.last_payment_amount) : null,
        next_billing_date: errorItem.data.next_billing_date || null
      }

      // Create the client first
      console.log('[ClientsPage Retry] Creating client:', clientData.email)
      const createResponse = await clientsAPI.create(clientData)
      const createdClient = createResponse.data?.client
      console.log('[ClientsPage Retry] Client created with ID:', createdClient?.client_id)

      // If card_id is provided, try to assign the card
      if (errorItem.data.card_id && errorItem.data.card_id.trim()) {
        const cardIdInput = errorItem.data.card_id.trim()
        console.log('[ClientsPage Retry] Attempting to assign card:', cardIdInput)

        // Fetch cards for lookup
        let allCards = []
        try {
          let nextToken = null
          do {
            const params = { org_id: currentOrg.org_id, limit: 100 }
            if (nextToken) params.nextToken = nextToken
            const response = await cardsAPI.getAll(params)
            const cardsData = response.data?.cards || []
            allCards = [...allCards, ...cardsData]
            nextToken = response.data?.nextToken
          } while (nextToken)
          console.log('[ClientsPage Retry] Fetched cards for assignment:', allCards.length)
        } catch (err) {
          throw new Error(`Failed to fetch cards: ${err.message}`)
        }

        // Find matching card by full ID or last 5 digits
        let matchingCard = null
        if (cardIdInput.length >= 10) {
          // Try exact match first (using card_id field, not rfid_card_number)
          matchingCard = allCards.find(card => card.card_id === cardIdInput)
          console.log('[ClientsPage Retry] Exact match by card_id:', matchingCard ? 'Found' : 'Not found')
        }

        if (!matchingCard && cardIdInput.length >= 1) {
          // Try matching by last 5 digits (using last_5_digits field)
          const cardsMatchingLast5 = allCards.filter(card =>
            card.last_5_digits === cardIdInput.slice(-5)
          )
          console.log('[ClientsPage Retry] Last 5 digits match count:', cardsMatchingLast5.length)

          if (cardsMatchingLast5.length === 1) {
            matchingCard = cardsMatchingLast5[0]
          } else if (cardsMatchingLast5.length > 1) {
            throw new Error(`Card ID '${cardIdInput}' matches multiple cards. Please use full card ID.`)
          }
        }

        if (!matchingCard) {
          console.error('[ClientsPage Retry] Card not found:', cardIdInput)
          throw new Error(`Card '${cardIdInput}' not found`)
        }

        console.log('[ClientsPage Retry] Matching card found:', matchingCard.card_id)

        // Check if card is already assigned
        if (matchingCard.assigned_to || matchingCard.client_id) {
          console.error('[ClientsPage Retry] Card already assigned:', matchingCard.card_id)
          throw new Error(`Card ${matchingCard.last_5_digits} is already assigned`)
        }

        // Assign the card to the newly created client (using card_id field)
        try {
          console.log('[ClientsPage Retry] Assigning card', matchingCard.card_id, 'to client', createdClient.client_id)
          await cardsAPI.assign(matchingCard.card_id, {
            client_id: createdClient.client_id
          })
          console.log('[ClientsPage Retry] Card assignment successful')
        } catch (assignErr) {
          // Card assignment failed, but client was created
          console.error('[ClientsPage Retry] Card assignment failed:', assignErr)
          throw new Error(`Client created but card assignment failed: ${assignErr.message}`)
        }
      }

      // Remove from errors, add to success
      const updatedErrors = importErrors.filter((_, i) => i !== index)
      setImportErrors(updatedErrors)
      setImportSuccess([...importSuccess, errorItem])
      await loadClients()
    } catch (err) {
      // Update error message
      console.error('[ClientsPage Retry] Row import failed:', err.message)
      const updatedErrors = [...importErrors]
      updatedErrors[index].errors = [err.message || 'Failed to import']
      setImportErrors(updatedErrors)
    } finally {
      setImporting(false)
      setEditingErrorIndex(null)
    }
  }

  // Bulk Delete handlers
  const toggleSelectForDelete = (clientId) => {
    setSelectedForDelete(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedForDelete.length === clients.length) {
      setSelectedForDelete([])
    } else {
      setSelectedForDelete(clients.map(c => c.client_id))
    }
  }

  const getClientsWithCards = () => {
    return selectedForDelete.filter(clientId => {
      const client = clients.find(c => c.client_id === clientId)
      return client?.rfid_card_number
    })
  }

  const handleBulkDelete = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm')
      return
    }

    const clientsWithCards = getClientsWithCards()
    if (clientsWithCards.length > 0 && !unassignCards) {
      setError(`${clientsWithCards.length} client(s) have assigned cards. Enable "Unassign cards first" option to proceed.`)
      return
    }

    setDeleting(true)
    setError('')

    try {
      // Unassign cards first if needed
      if (unassignCards && clientsWithCards.length > 0) {
        for (const clientId of clientsWithCards) {
          const client = clients.find(c => c.client_id === clientId)
          if (client?.rfid_card_number) {
            await cardsAPI.unassign(client.rfid_card_number)
          }
        }
      }

      // Delete all selected clients
      for (const clientId of selectedForDelete) {
        await clientsAPI.delete(clientId)
      }

      await loadClients()
      setShowBulkDeleteModal(false)
      setSelectedForDelete([])
      setDeleteConfirmation('')
      setUnassignCards(false)
    } catch (err) {
      setError(err.message || 'Failed to delete clients')
    } finally {
      setDeleting(false)
    }
  }

  const closeBulkDeleteModal = () => {
    setShowBulkDeleteModal(false)
    setSelectedForDelete([])
    setDeleteConfirmation('')
    setUnassignCards(false)
    setBulkDeleteSearch('')
    setError('')
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Clients <span style={{ fontSize: '1.25rem', fontWeight: '500', color: 'var(--text-light)' }}>({clients.length})</span></h1>
          <p>Manage client accounts and access cards</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', position: 'relative' }} ref={advancedMenuRef}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAdvancedMenu(!showAdvancedMenu)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Advanced
            <ChevronDown size={16} />
          </button>

          {showAdvancedMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '0.25rem',
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              minWidth: '180px',
              zIndex: 1000
            }}>
              <button
                onClick={() => {
                  setShowImportModal(true)
                  setShowAdvancedMenu(false)
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'var(--text)',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--background)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <Upload size={16} />
                Import CSV
              </button>
              <button
                onClick={() => {
                  setShowBulkDeleteModal(true)
                  setShowAdvancedMenu(false)
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  borderTop: '1px solid var(--border)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--background)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <Trash2 size={16} />
                Bulk Delete
              </button>
            </div>
          )}

          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} />
            Add Client
          </button>
        </div>
      </div>

      {error && !showDetailsModal && !showModal && !showImportModal && !showBulkDeleteModal && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        {/* Search and Filter Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          {/* Search bar with clear button */}
          <div style={{ position: 'relative', flex: '1 1 300px', minWidth: '250px' }}>
            <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem', paddingRight: searchTerm ? '2.5rem' : '0.75rem' }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-light)',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Status filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="statusFilter" style={{ fontSize: '0.875rem', fontWeight: '500', whiteSpace: 'nowrap' }}>
              Status:
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                minWidth: '120px'
              }}
            >
              <option value="all">All</option>
              <option value="current">Current</option>
              <option value="access-only">Access Only</option>
              <option value="new-client">New Client</option>
              <option value="on-hold">On Hold</option>
              <option value="canceled">Canceled</option>
              <option value="probation">Probation</option>
              <option value="bolo">BOLO</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          {/* Hide Canceled/Blocked toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Hide Canceled/Blocked
            </label>
          </div>

          {/* Include hidden in search */}
          {hideInactive && searchTerm && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={includeHiddenInSearch}
                  onChange={(e) => setIncludeHiddenInSearch(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Include hidden in search
              </label>
            </div>
          )}

          {/* Show per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            <label htmlFor="pageSize" style={{ fontSize: '0.875rem', fontWeight: '500', whiteSpace: 'nowrap' }}>
              Show per page:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                minWidth: '80px'
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={150}>150</option>
              <option value={200}>200</option>
              <option value={250}>250</option>
              <option value={300}>300</option>
              <option value={350}>350</option>
              <option value={400}>400</option>
              <option value={450}>450</option>
              <option value={500}>500</option>
              <option value={10000}>All</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div style={{
          fontSize: '0.875rem',
          color: 'var(--text-light)',
          marginBottom: '1rem',
          paddingLeft: '0.5rem'
        }}>
          Showing {Math.min(filteredClients.length, allFilteredClients.length)} of {allFilteredClients.length} clients
          {allFilteredClients.length !== totalClients && (
            <span style={{ marginLeft: '0.25rem' }}>
              (filtered from {totalClients} total)
            </span>
          )}
        </div>

        {filteredClients.length === 0 ? (
          <p className="no-data">No clients found</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('client_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Client ID
                      {sortColumn === 'client_id' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Name
                      {sortColumn === 'name' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Email
                      {sortColumn === 'email' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('mobile')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Mobile
                      {sortColumn === 'mobile' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('card_number')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Card #
                      {sortColumn === 'card_number' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('status_rfid')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Card Access
                      {sortColumn === 'status_rfid' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('client_status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Client Status
                      {sortColumn === 'client_status' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.client_id}>
                    <td>{client.client_id}</td>
                    <td>{client.first_name} {client.last_name}</td>
                    <td>{client.email || '-'}</td>
                    <td>{client.mobile ? formatPhone(client.mobile) : '-'}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                      {client.rfid_card_number ? client.rfid_card_number.slice(-5) : '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                        {client.rfid_card_number ? (
                          <span className={`badge ${
                            client.status_rfid === 'active' ? 'badge-success' : 'badge-danger'
                          }`}>
                            {client.status_rfid === 'active' ? 'Enabled' : 'Disabled'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-light)' }}>-</span>
                        )}
                        {client.client_status === 'current' && !client.rfid_card_number && (
                          <span className="badge" style={{
                            backgroundColor: '#a855f7',
                            color: '#fff',
                            fontSize: '0.7rem'
                          }} title="Current client without assigned card">
                            ⚠ No Card
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${
                        client.client_status === 'current' || client.client_status === 'access-only' ? 'badge-success' :
                        client.client_status === 'on-hold' ? 'badge-warning' :
                        client.client_status === 'probation' ? 'badge-warning' :
                        client.client_status === 'bolo' ? 'badge-warning' :
                        client.client_status === 'new-client' ? 'badge-info' :
                        'badge-danger'
                      }`}>
                        {client.client_status === 'new-client' ? 'New Client' : client.client_status || 'new-client'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {client.internal_note || client.external_note ? (
                        <button
                          onClick={() => handleOpenNoteModal(client)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '1.25rem',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            lineHeight: 1
                          }}
                          title="View notes"
                        >
                          📝
                        </button>
                      ) : '-'}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                        onClick={() => handleViewDetails(client)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Add New Client</h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="first_name">First Name *</label>
                <input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  onBlur={(e) => setFormData({ ...formData, first_name: capitalizeName(e.target.value) })}
                  style={{ borderColor: fieldErrors.first_name ? '#ef4444' : '' }}
                />
                {fieldErrors.first_name && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.first_name}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name *</label>
                <input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  onBlur={(e) => setFormData({ ...formData, last_name: capitalizeName(e.target.value) })}
                  style={{ borderColor: fieldErrors.last_name ? '#ef4444' : '' }}
                />
                {fieldErrors.last_name && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.last_name}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="text"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{ borderColor: fieldErrors.email ? '#ef4444' : '' }}
                />
                {fieldErrors.email && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.email}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile</label>
                <input
                  id="mobile"
                  type="tel"
                  value={formatPhone(formData.mobile)}
                  onChange={(e) => setFormData({ ...formData, mobile: normalizePhone(e.target.value) })}
                  placeholder="(XXX) XXX-XXXX"
                  maxLength="14"
                  style={{ borderColor: fieldErrors.mobile ? '#ef4444' : '' }}
                />
                {fieldErrors.mobile && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.mobile}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="legacy_client_id">Legacy Client ID</label>
                <input
                  id="legacy_client_id"
                  type="text"
                  value={formData.legacy_client_id || ''}
                  onChange={(e) => setFormData({ ...formData, legacy_client_id: e.target.value })}
                  placeholder="Old 3-digit ID (optional)"
                  maxLength="4"
                  style={{ borderColor: fieldErrors.legacy_client_id ? '#ef4444' : '' }}
                />
                <small style={{ color: '#6b7280' }}>Legacy Client ID from old system (1-4 digits, optional)</small>
                {fieldErrors.legacy_client_id && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.legacy_client_id}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="legacy_membership_plan">Legacy Membership Plan</label>
                <input
                  id="legacy_membership_plan"
                  type="text"
                  value={formData.legacy_membership_plan || ''}
                  onChange={(e) => setFormData({ ...formData, legacy_membership_plan: e.target.value })}
                  placeholder="Old membership plan name (optional)"
                  style={{ borderColor: fieldErrors.legacy_membership_plan ? '#ef4444' : '' }}
                />
                <small style={{ color: '#6b7280' }}>Membership plan from old system (optional)</small>
                {fieldErrors.legacy_membership_plan && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.legacy_membership_plan}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="hasMembership">Has Membership</label>
                <select
                  id="hasMembership"
                  value={formData.hasMembership || ''}
                  onChange={(e) => setFormData({ ...formData, hasMembership: e.target.value })}
                  style={{ borderColor: fieldErrors.hasMembership ? '#ef4444' : '' }}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <small style={{ color: '#6b7280' }}>Does client have an active membership?</small>
                {fieldErrors.hasMembership && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.hasMembership}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="cardLastValidated">Card Last Validated</label>
                <input
                  id="cardLastValidated"
                  type="date"
                  value={formData.cardLastValidated || ''}
                  onChange={(e) => setFormData({ ...formData, cardLastValidated: e.target.value })}
                  style={{ borderColor: fieldErrors.cardLastValidated ? '#ef4444' : '' }}
                />
                <small style={{ color: '#6b7280' }}>Date card was last validated (optional)</small>
                {fieldErrors.cardLastValidated && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {fieldErrors.cardLastValidated}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="internal_note">Internal Note</label>
                <textarea
                  key="internal_note"
                  id="internal_note"
                  value={formData.internal_note || ''}
                  onChange={(e) => setFormData({ ...formData, internal_note: e.target.value })}
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit' }}
                />
                <small style={{ color: '#6b7280' }}>Internal Note: Staff only</small>
              </div>

              <div className="form-group">
                <label htmlFor="external_note">External Note</label>
                <textarea
                  key="external_note"
                  id="external_note"
                  value={formData.external_note || ''}
                  onChange={(e) => setFormData({ ...formData, external_note: e.target.value })}
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit' }}
                />
                <small style={{ color: '#6b7280' }}>External Note: May be sent to client</small>
              </div>

              <div className="form-group">
                <label htmlFor="client_status">Client Status *</label>
                <select
                  id="client_status"
                  value={formData.client_status}
                  onChange={(e) => setFormData({ ...formData, client_status: e.target.value })}
                  required
                >
                  <optgroup label="Active">
                    <option value="current">Current</option>
                    <option value="access-only">Access Only</option>
                    <option value="bolo">BOLO</option>
                    <option value="probation">Probation</option>
                  </optgroup>
                  <optgroup label="Inactive">
                    <option value="new-client">New Client</option>
                    <option value="canceled">Canceled</option>
                    <option value="blocked">Blocked</option>
                    <option value="on-hold">On-hold</option>
                  </optgroup>
                </select>
                <small>Client membership status</small>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ flex: 1 }}
                >
                  {submitting ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Details Modal */}
      {showDetailsModal && selectedClient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Client Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  setSelectedClient(null)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Client ID
                  </label>
                  <p style={{ margin: 0, fontSize: '1rem', fontFamily: 'monospace', fontWeight: '600' }}>
                    {selectedClient.client_id}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Legacy Client ID
                  </label>
                  {isEditingClient ? (
                    <input
                      type="text"
                      value={editClientData.legacy_client_id || ''}
                      onChange={(e) => setEditClientData({ ...editClientData, legacy_client_id: e.target.value })}
                      placeholder="Old ID (optional)"
                      maxLength="4"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: '1rem', fontFamily: 'monospace' }}>
                      {selectedClient.legacy_client_id || '-'}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Legacy Membership Plan
                </label>
                {isEditingClient ? (
                  <input
                    type="text"
                    value={editClientData.legacy_membership_plan || ''}
                    onChange={(e) => setEditClientData({ ...editClientData, legacy_membership_plan: e.target.value })}
                    placeholder="Old membership plan"
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                ) : (
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    {selectedClient.legacy_membership_plan || '-'}
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Has Membership
                  </label>
                  {isEditingClient ? (
                    <select
                      value={editClientData.hasMembership || ''}
                      onChange={(e) => setEditClientData({ ...editClientData, hasMembership: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                      <option value="">Select...</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <p style={{ margin: 0, fontSize: '1rem' }}>
                      {selectedClient.hasMembership ? selectedClient.hasMembership.charAt(0).toUpperCase() + selectedClient.hasMembership.slice(1) : '-'}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Card Last Validated
                  </label>
                  {isEditingClient ? (
                    <input
                      type="date"
                      value={editClientData.cardLastValidated || ''}
                      onChange={(e) => setEditClientData({ ...editClientData, cardLastValidated: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: '1rem' }}>
                      {selectedClient.cardLastValidated || '-'}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    First Name
                  </label>
                  {isEditingClient ? (
                    <input
                      type="text"
                      value={editClientData.first_name}
                      onChange={(e) => setEditClientData({ ...editClientData, first_name: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      required
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: '1rem' }}>
                      {selectedClient.first_name}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Last Name
                  </label>
                  {isEditingClient ? (
                    <input
                      type="text"
                      value={editClientData.last_name}
                      onChange={(e) => setEditClientData({ ...editClientData, last_name: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      required
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: '1rem' }}>
                      {selectedClient.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Email
                </label>
                {isEditingClient ? (
                  <input
                    type="email"
                    value={editClientData.email}
                    onChange={(e) => setEditClientData({ ...editClientData, email: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                ) : (
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    {selectedClient.email || '-'}
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Mobile
                </label>
                {isEditingClient ? (
                  <input
                    type="tel"
                    value={formatPhone(editClientData.mobile)}
                    onChange={(e) => setEditClientData({ ...editClientData, mobile: normalizePhone(e.target.value) })}
                    placeholder="(XXX) XXX-XXXX"
                    maxLength="14"
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                ) : (
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    {selectedClient.mobile ? formatPhone(selectedClient.mobile) : '-'}
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Internal Note
                </label>
                {isEditingClient ? (
                  <>
                    <textarea
                      value={editClientData.internal_note}
                      onChange={(e) => setEditClientData({ ...editClientData, internal_note: e.target.value })}
                      rows="3"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit' }}
                    />
                    <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
                      Internal Note: Staff only
                    </small>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                    {selectedClient.internal_note || '-'}
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                  External Note
                </label>
                {isEditingClient ? (
                  <>
                    <textarea
                      value={editClientData.external_note}
                      onChange={(e) => setEditClientData({ ...editClientData, external_note: e.target.value })}
                      rows="3"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit' }}
                    />
                    <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
                      External Note: May be sent to client
                    </small>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                    {selectedClient.external_note || '-'}
                  </p>
                )}
              </div>

              {/* Payment Tracking Section */}
              <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text)' }}>
                  Payment Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Membership Plan
                    </label>
                    {isEditingClient ? (
                      <input
                        type="text"
                        value={editClientData.membership_plan}
                        onChange={(e) => setEditClientData({ ...editClientData, membership_plan: e.target.value })}
                        placeholder="e.g., Monthly, Annual"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      />
                    ) : (
                      <p style={{ margin: 0, fontSize: '1rem' }}>
                        {selectedClient.membership_plan || '-'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Payment Method
                    </label>
                    {isEditingClient ? (
                      <select
                        value={editClientData.payment_method}
                        onChange={(e) => setEditClientData({ ...editClientData, payment_method: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      >
                        <option value="none">None</option>
                        <option value="stripe_card">Stripe (Credit Card)</option>
                        <option value="venmo">Venmo</option>
                        <option value="cash">Cash</option>
                        <option value="other">Other</option>
                      </select>
                    ) : (
                      <p style={{ margin: 0, fontSize: '1rem' }}>
                        {selectedClient.payment_method === 'stripe_card' ? 'Stripe (Credit Card)' :
                         selectedClient.payment_method === 'venmo' ? 'Venmo' :
                         selectedClient.payment_method === 'cash' ? 'Cash' :
                         selectedClient.payment_method === 'other' ? 'Other' : '-'}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Payment Status
                    </label>
                    {isEditingClient ? (
                      <select
                        value={editClientData.payment_status}
                        onChange={(e) => setEditClientData({ ...editClientData, payment_status: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      >
                        <option value="none">None</option>
                        <option value="current">Current</option>
                        <option value="overdue">Overdue</option>
                        <option value="outstanding">Outstanding</option>
                      </select>
                    ) : (
                      <span className={`badge ${
                        selectedClient.payment_status === 'current' ? 'badge-success' :
                        selectedClient.payment_status === 'overdue' ? 'badge-danger' :
                        selectedClient.payment_status === 'outstanding' ? 'badge-warning' :
                        'badge-info'
                      }`}>
                        {selectedClient.payment_status === 'none' ? 'None' : selectedClient.payment_status || 'None'}
                      </span>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Last Payment Amount
                    </label>
                    {isEditingClient ? (
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={editClientData.last_payment_amount}
                        onChange={(e) => setEditClientData({ ...editClientData, last_payment_amount: e.target.value })}
                        placeholder="0 or 0.00"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      />
                    ) : (
                      <p style={{ margin: 0, fontSize: '1rem' }}>
                        {selectedClient.last_payment_amount ? `$${Number(selectedClient.last_payment_amount).toFixed(2)}` : '-'}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Last Payment Date
                    </label>
                    {isEditingClient ? (
                      <input
                        type="date"
                        value={editClientData.last_payment_date}
                        onChange={(e) => setEditClientData({ ...editClientData, last_payment_date: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      />
                    ) : (
                      <p style={{ margin: 0, fontSize: '1rem' }}>
                        {selectedClient.last_payment_date ? new Date(selectedClient.last_payment_date).toLocaleDateString() : '-'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Next Billing Date
                    </label>
                    {isEditingClient ? (
                      <input
                        type="date"
                        value={editClientData.next_billing_date}
                        onChange={(e) => setEditClientData({ ...editClientData, next_billing_date: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      />
                    ) : (
                      <p style={{ margin: 0, fontSize: '1rem' }}>
                        {selectedClient.next_billing_date ? new Date(selectedClient.next_billing_date).toLocaleDateString() : '-'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    RFID Card Access
                  </label>
                  {selectedClient.rfid_card_number ? (
                    <>
                      <span className={`badge ${
                        selectedClient.status_rfid === 'active' ? 'badge-success' : 'badge-danger'
                      }`}>
                        {selectedClient.status_rfid === 'active' ? 'Enabled' : 'Disabled'}
                      </span>
                      {isEditingClient && (
                        <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
                          Automatically set based on Client Status (Active = Enabled, Inactive = Disabled)
                        </small>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-light)' }}>
                      No card assigned
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Client Status
                  </label>
                  {isEditingClient ? (
                    <select
                      value={editClientData.client_status}
                      onChange={(e) => {
                        const newStatus = e.target.value
                        setEditClientData({ ...editClientData, client_status: newStatus })

                        // Show inline warning based on status change
                        const activeStatuses = ['current', 'bolo', 'probation']
                        const wasActive = activeStatuses.includes(selectedClient.client_status)
                        const willBeActive = activeStatuses.includes(newStatus)

                        if (selectedClient.rfid_card_number) {
                          if (wasActive && !willBeActive) {
                            setStatusChangeWarning('⚠️ Changing to inactive status will disable card access')
                          } else if (!wasActive && willBeActive) {
                            setStatusChangeWarning('✓ Changing to active status will enable card access')
                          } else {
                            setStatusChangeWarning('')
                          }
                        } else {
                          setStatusChangeWarning('')
                        }
                      }}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                      <optgroup label="Active">
                        <option value="current">Current</option>
                        <option value="access-only">Access Only</option>
                        <option value="bolo">BOLO</option>
                        <option value="probation">Probation</option>
                      </optgroup>
                      <optgroup label="Inactive">
                        <option value="new-client">New Client</option>
                        <option value="canceled">Canceled</option>
                        <option value="blocked">Blocked</option>
                        <option value="on-hold">On-hold</option>
                      </optgroup>
                    </select>
                  ) : (
                    <span className={`badge ${
                      selectedClient.client_status === 'current' || selectedClient.client_status === 'access-only' ? 'badge-success' :
                      selectedClient.client_status === 'bolo' || selectedClient.client_status === 'probation' ? 'badge-warning' :
                      selectedClient.client_status === 'new-client' ? 'badge-info' :
                      'badge-danger'
                    }`}>
                      {selectedClient.client_status === 'new-client' ? 'New Client' : selectedClient.client_status || 'new-client'}
                    </span>
                  )}
                  {isEditingClient && statusChangeWarning && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: statusChangeWarning.includes('disable') ? '#fef3c7' : '#d1fae5',
                      color: statusChangeWarning.includes('disable') ? '#92400e' : '#065f46',
                      border: `1px solid ${statusChangeWarning.includes('disable') ? '#fcd34d' : '#6ee7b7'}`
                    }}>
                      {statusChangeWarning}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                  RFID Card Number
                </label>
                {selectedClient.rfid_card_number ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '1rem', fontFamily: 'monospace' }}>
                          {selectedClient.rfid_card_number}
                        </p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                          Last 5 digits: <span style={{ fontFamily: 'monospace', fontWeight: '600', color: 'var(--text)' }}>{selectedClient.rfid_card_number.slice(-5)}</span>
                        </p>
                      </div>
                      {!isEditingClient && (
                        <button
                          className="btn btn-secondary"
                          onClick={handleUnassignCardFromClient}
                          disabled={submitting}
                          style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}
                        >
                          <UserMinus size={16} style={{ marginRight: '0.25rem' }} />
                          Unassign
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '1rem', fontFamily: 'monospace' }}>
                    No card assigned
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Created
                  </label>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleString() : '-'}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Last Updated
                  </label>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedClient.updated_at ? new Date(selectedClient.updated_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </div>

            {error && <div className="alert alert-danger" style={{ marginTop: '1.5rem' }}>{error}</div>}

            <div style={{ marginTop: '2rem', display: 'flex', gap: '0.75rem' }}>
              {isEditingClient ? (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditingClient(false)
                      setError('')
                      setEditClientData({
                        first_name: selectedClient.first_name || '',
                        last_name: selectedClient.last_name || '',
                        email: selectedClient.email || '',
                        mobile: selectedClient.mobile || '',
                        status_rfid: selectedClient.status_rfid || 'inactive',
                        client_status: selectedClient.client_status || 'new-client',
                        internal_note: selectedClient.internal_note || '',
                        external_note: selectedClient.external_note || '',
                        membership_plan: selectedClient.membership_plan || '',
                        payment_method: selectedClient.payment_method || 'none',
                        payment_status: selectedClient.payment_status || 'none',
                        last_payment_date: selectedClient.last_payment_date || '',
                        last_payment_amount: selectedClient.last_payment_amount || '',
                        next_billing_date: selectedClient.next_billing_date || ''
                      })
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveClientStatus}
                    disabled={submitting}
                    style={{ marginLeft: 'auto' }}
                  >
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteClient}
                    disabled={submitting}
                  >
                    Delete
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setIsEditingClient(true)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDetailsModal(false)
                      setSelectedClient(null)
                      setIsEditingClient(false)
                      setError('')
                    }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && noteModalClient && (
        <NoteModal
          client={noteModalClient}
          onClose={() => setShowNoteModal(false)}
          onSave={handleSaveNote}
        />
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Import Clients from CSV</h2>
              <button
                onClick={resetImport}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text)',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {!csvFile && !importPreview && importErrors.length === 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  backgroundColor: 'var(--background)',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  border: '1px solid var(--border)'
                }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.75rem', fontWeight: '500' }}>
                    CSV Format Requirements:
                  </p>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', lineHeight: '1.6' }}>
                    <p style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '0.5rem', marginTop: '0' }}>Required columns:</p>
                    <ul style={{ margin: '0 0 1rem 1.25rem', paddingLeft: 0 }}>
                      <li><strong>first_name</strong> - First Name</li>
                      <li><strong>last_name</strong> - Last Name</li>
                      <li><strong>email</strong> - Email: Must be valid format</li>
                      <li><strong>mobile</strong> - Mobile: Must be 10 digits (formatting optional)</li>
                      <li><strong>client_status</strong> - Valid client statuses: current, access-only, new-client, on-hold, canceled, probation, bolo, blocked</li>
                    </ul>

                    <p style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '0.5rem', marginTop: '0' }}>Optional columns:</p>
                    <ul style={{ margin: '0 0 1rem 1.25rem', paddingLeft: 0 }}>
                      <li><strong>card_id:</strong> Full card ID or last 5 digits to assign a card</li>
                      <li><strong>legacy_client_id:</strong> Old system client ID (1-4 digits)</li>
                      <li><strong>legacy_membership_plan:</strong> Old membership plan name</li>
                      <li><strong>hasMembership:</strong> "yes" or "no"</li>
                      <li><strong>cardLastValidated:</strong> Date in YYYY-MM-DD format</li>
                      <li><strong>internal_note:</strong> Staff-only administrative notes</li>
                    </ul>

                    <p style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '0.5rem', marginTop: '0' }}>Stripe-related Data:</p>
                    <ul style={{ margin: '0 0 1rem 1.25rem', paddingLeft: 0 }}>
                      <li>membership_plan, payment_method, payment_status</li>
                      <li>last_payment_date, last_payment_amount, next_billing_date</li>
                    </ul>

                    <p style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '0.5rem', marginTop: '0' }}>Notes:</p>
                    <ul style={{ margin: '0 0 0.75rem 1.25rem', paddingLeft: 0 }}>
                      <li>Card ID: Card must exist and be unassigned</li>
                      <li>All Dates: Use YYYY-MM-DD format (e.g., 2025-01-15)</li>
                    </ul>
                  </div>
                  <button
                    onClick={downloadSampleCSV}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                  >
                    Download Sample CSV
                  </button>
                </div>

                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--text)'
                  }}
                />
              </div>
            )}

            {importPreview && importPreview.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--success)' }}>
                  Valid Rows ({importPreview.length})
                </h3>
                <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.5rem' }}>
                  {importPreview.map((item, idx) => (
                    <div key={idx} style={{ fontSize: '0.875rem', padding: '0.25rem 0', borderBottom: idx < importPreview.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      Row {item.row}: {item.data.first_name} {item.data.last_name} ({item.data.email})
                      {item.data.card_id && item.data.card_id.trim() && (
                        <span style={{ marginLeft: '0.5rem', color: 'var(--text-light)', fontFamily: 'monospace' }}>
                          | Card: {item.data.card_id}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importErrors && importErrors.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--danger)' }}>
                  Invalid Rows ({importErrors.length}) - Click to edit and retry
                </h3>
                <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid var(--danger)', borderRadius: '4px', padding: '0.5rem' }}>
                  {importErrors.map((item, idx) => (
                    <div key={idx} style={{ fontSize: '0.875rem', padding: '0.75rem', marginBottom: '0.5rem', backgroundColor: 'var(--background)', borderRadius: '4px', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong>Row {item.row}</strong>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {editingErrorIndex === idx ? (
                            <>
                              <button
                                onClick={() => retryImportErrorRow(idx)}
                                className="btn btn-primary"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                disabled={importing}
                              >
                                {importing ? 'Saving...' : 'Retry Import'}
                              </button>
                              <button
                                onClick={() => setEditingErrorIndex(null)}
                                className="btn btn-secondary"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setEditingErrorIndex(idx)}
                              className="btn btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>

                      {editingErrorIndex === idx ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>First Name</label>
                            <input
                              type="text"
                              value={item.data.first_name}
                              onChange={(e) => updateErrorRow(idx, 'first_name', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Last Name</label>
                            <input
                              type="text"
                              value={item.data.last_name}
                              onChange={(e) => updateErrorRow(idx, 'last_name', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Email</label>
                            <input
                              type="text"
                              value={item.data.email}
                              onChange={(e) => updateErrorRow(idx, 'email', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Mobile</label>
                            <input
                              type="text"
                              value={item.data.mobile}
                              onChange={(e) => updateErrorRow(idx, 'mobile', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Client Status (optional)</label>
                            <select
                              value={item.data.client_status || ''}
                              onChange={(e) => updateErrorRow(idx, 'client_status', e.target.value)}
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)' }}
                            >
                              <option value="">new-client (default)</option>
                              <option value="current">current</option>
                              <option value="bolo">bolo</option>
                              <option value="probation">probation</option>
                              <option value="canceled">canceled</option>
                              <option value="blocked">blocked</option>
                              <option value="on-hold">on-hold</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>Card ID (optional)</label>
                            <input
                              type="text"
                              value={item.data.card_id || ''}
                              onChange={(e) => updateErrorRow(idx, 'card_id', e.target.value)}
                              placeholder="Full ID or last 5 digits"
                              style={{ width: '100%', padding: '0.25rem 0.5rem', fontSize: '0.875rem', borderRadius: '4px', border: '1px solid var(--border)', fontFamily: 'monospace' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                          {item.data?.first_name} {item.data?.last_name} | {item.data?.email} | {item.data?.mobile}
                          {item.data?.card_id && item.data.card_id.trim() && (
                            <span style={{ marginLeft: '0.5rem', color: 'var(--text-light)', fontFamily: 'monospace' }}>
                              | Card: {item.data.card_id}
                            </span>
                          )}
                        </div>
                      )}

                      <ul style={{ margin: '0 0 0 1.5rem', padding: 0, color: 'var(--danger)' }}>
                        {item.errors.map((err, errIdx) => (
                          <li key={errIdx} style={{ fontSize: '0.75rem' }}>{err}</li>
                        ))}
                        {item.error && <li style={{ fontSize: '0.75rem' }}>{item.error}</li>}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importSuccess && importSuccess.length > 0 && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#d1fae5', color: '#047857', borderRadius: '4px', fontSize: '1rem', fontWeight: '500' }}>
                Successfully imported {importSuccess.length} client(s)
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={resetImport}>
                {importSuccess && importSuccess.length > 0 ? 'Done' : 'Cancel'}
              </button>
              {importPreview && importPreview.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={importing}
                >
                  {importing ? 'Importing...' : `Import ${importPreview.length} Client(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '8px',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            color: 'var(--text)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Bulk Delete Clients</h2>
              <button
                onClick={closeBulkDeleteModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  placeholder="Search clients by name, email, phone, or status..."
                  value={bulkDeleteSearch}
                  onChange={(e) => setBulkDeleteSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--text)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el) {
                        const filteredClients = clients.filter(c =>
                          `${c.first_name} ${c.last_name} ${c.email} ${c.mobile} ${c.client_status}`
                            .toLowerCase()
                            .includes(bulkDeleteSearch.toLowerCase())
                        )
                        const filteredIds = filteredClients.map(c => c.client_id)
                        const selectedFilteredCount = filteredIds.filter(id => selectedForDelete.includes(id)).length
                        const allFilteredSelected = selectedFilteredCount === filteredClients.length && filteredClients.length > 0
                        const someFilteredSelected = selectedFilteredCount > 0 && selectedFilteredCount < filteredClients.length

                        el.checked = allFilteredSelected
                        el.indeterminate = someFilteredSelected
                      }
                    }}
                    onChange={() => {
                      const filteredClients = clients.filter(c =>
                        `${c.first_name} ${c.last_name} ${c.email} ${c.mobile} ${c.client_status}`
                          .toLowerCase()
                          .includes(bulkDeleteSearch.toLowerCase())
                      )
                      const filteredIds = filteredClients.map(c => c.client_id)
                      const allFilteredSelected = filteredIds.every(id => selectedForDelete.includes(id))

                      if (allFilteredSelected) {
                        // Deselect all filtered
                        setSelectedForDelete(selectedForDelete.filter(id => !filteredIds.includes(id)))
                      } else {
                        // Select all filtered (add to existing selection)
                        setSelectedForDelete([...new Set([...selectedForDelete, ...filteredIds])])
                      }
                    }}
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span style={{ fontWeight: '600' }}>
                    {bulkDeleteSearch ? 'Select All in Search Results' : 'Select All'}
                    {' '}({clients.filter(c =>
                      `${c.first_name} ${c.last_name} ${c.email} ${c.mobile} ${c.client_status}`
                        .toLowerCase()
                        .includes(bulkDeleteSearch.toLowerCase())
                    ).length})
                  </span>
                </label>
                <span style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  {selectedForDelete.length} client(s) selected total
                </span>
              </div>

              <div style={{
                maxHeight: '300px',
                overflow: 'auto',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                padding: '0.5rem'
              }}>
                {clients
                  .filter(client =>
                    `${client.first_name} ${client.last_name} ${client.email} ${client.mobile} ${client.client_status}`
                      .toLowerCase()
                      .includes(bulkDeleteSearch.toLowerCase())
                  )
                  .map(client => (
                  <div
                    key={client.client_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem',
                      marginBottom: '0.25rem',
                      backgroundColor: selectedForDelete.includes(client.client_id) ? 'var(--background)' : 'transparent',
                      borderRadius: '4px'
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedForDelete.includes(client.client_id)}
                        onChange={() => toggleSelectForDelete(client.client_id)}
                        style={{ width: '1rem', height: '1rem' }}
                      />
                    </label>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {client.first_name} {client.last_name}
                        </span>
                        <span className={`badge ${
                          client.client_status === 'current' || client.client_status === 'access-only' ? 'badge-success' :
                          client.client_status === 'bolo' || client.client_status === 'probation' ? 'badge-warning' :
                          client.client_status === 'new-client' ? 'badge-info' :
                          'badge-danger'
                        }`} style={{ fontSize: '0.625rem' }}>
                          {client.client_status === 'new-client' ? 'New Client' : client.client_status || 'new-client'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                        {client.email} | {client.mobile ? formatPhone(client.mobile) : 'No phone'}
                      </div>
                    </div>
                    {client.rfid_card_number && (
                      <span className="badge badge-warning" style={{ fontSize: '0.75rem' }}>
                        Has Card
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {getClientsWithCards().length > 0 && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: 'var(--background)',
                border: '2px solid #f59e0b',
                borderRadius: '4px'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={unassignCards}
                    onChange={(e) => setUnassignCards(e.target.checked)}
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '0.9375rem', color: 'var(--text)' }}>
                    {getClientsWithCards().length} client(s) have assigned cards.
                    Check this box to automatically unassign cards before deletion.
                  </span>
                </label>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Type "delete" to confirm deletion of {selectedForDelete.length} client(s):
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type delete to confirm"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--surface)',
                  color: 'var(--text)'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-danger"
                onClick={handleBulkDelete}
                disabled={selectedForDelete.length === 0 || deleting}
              >
                {deleting ? 'Deleting...' : `Delete ${selectedForDelete.length} Client(s)`}
              </button>
              <button className="btn btn-secondary" onClick={closeBulkDeleteModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientsPage
